import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Compass, ArrowLeft, BookOpen, Search } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "home_care", label: "Home Care" },
  { value: "legal_financial", label: "Legal & Financial" },
  { value: "emotional_support", label: "Emotional Support" },
  { value: "medical_care", label: "Medical Care" }
];

export default function LibraryPage() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    loadArticles();
  }, []);

  useEffect(() => {
    filterArticles();
  }, [selectedCategory, searchQuery, articles]);

  const loadArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`);
      setArticles(response.data);
      setFilteredArticles(response.data);
    } catch (error) {
      console.error("Error loading articles:", error);
      toast.error("Failed to load articles");
    } finally {
      setIsLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = [...articles];

    if (selectedCategory) {
      filtered = filtered.filter(a => a.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(a =>
        a.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.content.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredArticles(filtered);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      {/* Header */}
      <header className="bg-white border-b border-[#E2E8F0] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                data-testid="back-to-home-button"
                onClick={() => navigate("/")}
                variant="ghost"
                className="text-primary hover:bg-primary/5"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-primary" strokeWidth={2} />
                <h1 className="text-xl font-heading font-bold text-primary">Care Library</h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
              <Input
                data-testid="search-articles-input"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search articles..."
                className="pl-10 h-12 text-base rounded-lg border-2 border-input focus:border-primary font-body"
              />
            </div>
            <select
              data-testid="category-filter"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="h-12 text-base rounded-lg border-2 border-input focus:border-primary font-body px-4"
            >
              <option value="">All Categories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Articles Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-lg text-text-secondary font-body">Loading articles...</p>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm p-12 text-center">
            <BookOpen className="w-16 h-16 text-muted mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-semibold text-text-primary mb-3">
              No Articles Found
            </h2>
            <p className="text-base text-text-secondary font-body mb-6">
              {searchQuery || selectedCategory ? "Try adjusting your search or filters." : "Articles will appear here soon."}
            </p>
          </div>
        ) : (
          <div data-testid="articles-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredArticles.map((article) => (
              <div
                key={article.id}
                data-testid={`article-card-${article.id}`}
                onClick={() => navigate(`/article/${article.id}`)}
                className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm hover:shadow-md transition-all duration-300 p-6 cursor-pointer border-l-4 border-l-secondary"
              >
                <div className="mb-3">
                  <span className="px-3 py-1 bg-accent/10 text-accent text-xs font-body font-semibold rounded-full">
                    {CATEGORIES.find(c => c.value === article.category)?.label || article.category}
                  </span>
                </div>
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-3">
                  {article.title}
                </h3>
                <p className="text-sm text-text-secondary font-body mb-4 line-clamp-3">
                  {article.content.substring(0, 150)}...
                </p>
                <div className="flex items-center justify-between text-xs text-text-secondary">
                  <span className="font-body">By {article.author_name}</span>
                  <span className="font-body">
                    {new Date(article.published_at || article.created_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
